import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, ArrowRight } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const parsePrefill = (content) => {
  const m = content.match(/BOOKING_PREFILL:\s*(\{[^\n]*\})/);
  if (!m) return { text: content, prefill: null };
  try {
    return { text: content.replace(m[0], "").trim(), prefill: JSON.parse(m[1]) };
  } catch {
    return { text: content.replace(m[0], "").trim(), prefill: null };
  }
};

export function ConciergeChat() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("aureus_concierge_session") || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (!open || loaded || !sessionId) return;
    setLoaded(true);
    fetch(`${API_BASE}/api/concierge/history?session_id=${sessionId}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, [open, loaded, sessionId]);

  if (pathname.startsWith("/admin")) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API_BASE}/api/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          let payload;
          try {
            payload = JSON.parse(part.slice(6));
          } catch {
            continue;
          }
          if (payload.delta) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + payload.delta };
              return c;
            });
          }
          if (payload.session_id) {
            setSessionId(payload.session_id);
            localStorage.setItem("aureus_concierge_session", payload.session_id);
          }
          if (payload.error) {
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: payload.error };
              return c;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: "assistant",
          content: "The concierge is momentarily away — please call +1 (786) 874-0320 or email concierge@aureusluxury.com.",
        };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm border border-[#2E3340] bg-[#16181D] shadow-2xl shadow-black/60 flex flex-col overflow-hidden"
            style={{ height: "min(540px, calc(100vh - 8rem))" }}
            data-testid="concierge-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E3340] bg-[#1E2128]">
              <div>
                <p className="font-serif-lux text-lg text-amber-50 leading-none">The Concierge</p>
                <p className="font-mono-lux text-[9px] uppercase tracking-[0.3em] text-amber-500 mt-1.5">AUREUS · Always on</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="concierge-close"
                className="text-zinc-500 hover:text-amber-300 transition-colors"
                aria-label="Close concierge"
              >
                <X size={18} />
              </button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="concierge-messages">
              {messages.length === 0 && (
                <div className="border border-[#2E3340] bg-[#1E2128] px-4 py-3 max-w-[85%]">
                  <p className="text-sm text-zinc-200 leading-relaxed" data-testid="concierge-welcome">
                    Welcome to AUREUS — I'm your concierge. Whether it's a 5am departure, a wedding entrance, or
                    simply arriving like you mean it, consider it arranged. What shall we organise?
                  </p>
                </div>
              )}
              {messages.map((m, i) => {
                if (m.role === "user") {
                  return (
                    <div key={i} className="flex justify-end">
                      <p className="bg-amber-500 text-black text-sm font-medium px-4 py-2.5 max-w-[85%] leading-relaxed">{m.content}</p>
                    </div>
                  );
                }
                const { text, prefill } = parsePrefill(m.content);
                return (
                  <div key={i} className="border border-[#2E3340] bg-[#1E2128] px-4 py-3 max-w-[85%]">
                    <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">
                      {text}
                      {streaming && i === messages.length - 1 && <span className="inline-block w-1.5 h-4 bg-amber-500/70 ml-0.5 animate-pulse align-text-bottom" />}
                    </p>
                    {prefill && (
                      <Link
                        to={`/book?${new URLSearchParams(prefill).toString()}`}
                        data-testid="concierge-prefill-link"
                        className="mt-3 inline-flex items-center gap-2 font-mono-lux text-[10px] font-semibold uppercase tracking-[0.2em] px-4 py-2.5 bg-amber-500 text-black hover:bg-amber-300 transition-colors"
                      >
                        Continue to booking <ArrowRight size={12} />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="border-t border-[#2E3340] p-3 flex gap-2 bg-[#1E2128]">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask anything — rates, fleet, occasions…"
                data-testid="concierge-input"
                className="flex-1 bg-[#0F1012] border border-[#2E3340] focus:border-amber-500/60 outline-none px-3.5 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 transition-colors"
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                data-testid="concierge-send"
                className="px-4 bg-amber-500 text-black hover:bg-amber-300 transition-colors disabled:opacity-40"
                aria-label="Send message"
              >
                <Send size={15} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="concierge-toggle"
        aria-label="Open the concierge"
        className="fixed bottom-5 right-4 sm:right-6 z-50 w-14 h-14 rounded-full bg-amber-500 text-black shadow-lg shadow-amber-500/20 hover:bg-amber-300 hover:scale-105 transition-all duration-300 flex items-center justify-center"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} strokeWidth={1.75} />}
      </button>
    </>
  );
}
